// NodeRichTextField.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { normalizeRichTextValue, serializeRichTextDocument } from 'utilities/RichText';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';

// CSS
import './NodeRichTextField.css';

// Components
import AddModal from 'components/addmodal/AddModal';
import RichTextEditor from 'components/richtext/RichTextEditor';
import RichTextRenderer from 'components/richtext/RichTextRenderer';
import CameraModal from 'editor/nodeadd/camera/CameraModal';

const toEmbeddedNodeCandidate = (node, baseKey) => {
    if (!node) return null;

    const nodeKey = String(node?.nodeKey || node?.key || '').trim();
    if (!nodeKey) return null;

    return {
        key: nodeKey,
        baseKey: String(node?.baseKey || baseKey || '').trim(),
        type: String(node?.nodeType || node?.type || 'NODE').trim() || 'NODE',
        title: String(node?.title || '').trim(),
        subtitle: String(node?.subtitle ?? node?.description ?? '').trim(),
        coverStoragePath: String(node?.coverStoragePath || '').trim(),
        coverUrl: String(node?.coverUrl || '').trim(),
    };
};

/**
 * Editable rich text field (toolbar, insert-node, blur-commit).
 */
const NodeRichTextFieldEditor = React.memo(({
    autoFocus = false,
    autoFocusKey = '',
    field,
    fieldLabel,
    onChange,
    value,
}) => {
    const language = useCurrentLanguage();
    const { setCurrentNode, setCurrentSection } = useNavigation();
    const { getType } = useNodeTypes();
    const { user } = useUser();
    const { activeBase } = useBase();
    const navigate = useNavigate();

    const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);

    const normalizedValue = useMemo(() => normalizeRichTextValue(value), [value]);
    const normalizedSerializedValue = useMemo(
        () => serializeRichTextDocument(normalizedValue.content),
        [normalizedValue.content],
    );
    const placeholder = useMemo(() => {
        const label = String(fieldLabel || field?.label || field?.name || '').trim();
        return label ? `Write ${label.toLowerCase()}...` : 'Write...';
    }, [field?.label, field?.name, fieldLabel]);

    const [draftValue, setDraftValue] = useState(normalizedValue);
    const [isDirty, setIsDirty] = useState(false);

    const editorRef = useRef(null);
    const committedSerializedRef = useRef(normalizedSerializedValue);

    useEffect(() => {
        const nextSerialized = normalizedSerializedValue;
        const previousSerialized = committedSerializedRef.current;

        committedSerializedRef.current = nextSerialized;

        if (isDirty && nextSerialized === previousSerialized) return;

        setDraftValue(normalizedValue);
        setIsDirty(false);
    }, [isDirty, normalizedSerializedValue, normalizedValue]);

    const handleInsertNodes = useCallback((rows = []) => {
        let insertedAny = false;

        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const candidate = toEmbeddedNodeCandidate(row, activeBase?.key || '');
            if (!candidate) return;
            insertedAny = editorRef.current?.insertEmbeddedNode(candidate) || insertedAny;
        });

        if (insertedAny) {
            setIsInsertModalOpen(false);
            editorRef.current?.focus();
        }
    }, [activeBase?.key]);

    const {
        cameraOpen,
        cameraContentType,
        handleCameraContentCreated,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        pendingUploadAccept,
        pendingUploadMultiple,
        setCameraOpen,
        uploadInputRef,
    } = useTopLevelNodeCreate({
        activeBaseKey: activeBase?.key || null,
        getType,
        language,
        navigate,
        onNodesCreated: handleInsertNodes,
        setCurrentNode,
        setCurrentSection,
        shouldNavigate: false,
        user,
    });

    const handleOpenInsertModal = useCallback(() => {
        setIsInsertModalOpen(true);
    }, []);

    const handleEditorChange = useCallback((nextValue) => {
        const normalizedNextValue = normalizeRichTextValue(nextValue);
        const nextSerialized = serializeRichTextDocument(normalizedNextValue.content);

        setDraftValue(normalizedNextValue);
        setIsDirty(nextSerialized !== committedSerializedRef.current);
    }, []);

    const handleEditorBlur = useCallback((nextValue) => {
        const normalizedNextValue = normalizeRichTextValue(nextValue);
        const nextSerialized = serializeRichTextDocument(normalizedNextValue.content);

        setDraftValue(normalizedNextValue);

        if (nextSerialized === committedSerializedRef.current) {
            setIsDirty(false);
            return;
        }

        committedSerializedRef.current = nextSerialized;
        setIsDirty(false);
        onChange?.(normalizedNextValue);
    }, [onChange]);

    const handleSelectExisting = useCallback((node) => {
        handleInsertNodes([node]);
    }, [handleInsertNodes]);

    const handleSelectType = useCallback((typeKey) => {
        handleTopLevelActionSelect(typeKey, {
            shouldNavigate: false,
        });
    }, [handleTopLevelActionSelect]);

    return (
        <div className="node-rich-text-field-container">
            <RichTextEditor
              ref={editorRef}
              autoFocus={autoFocus}
              autoFocusKey={autoFocusKey}
              compact
              placeholder={placeholder}
              value={draftValue}
              onBlur={handleEditorBlur}
              onChange={handleEditorChange}
              onInsertNodeRequest={handleOpenInsertModal}
            />

            <AddModal
              insertMode
              isOpen={isInsertModalOpen}
              node={null}
              onClose={() => setIsInsertModalOpen(false)}
              onExistingSelected={handleSelectExisting}
              onTypeSelect={handleSelectType}
            />

            <input
              style={{
                    display: 'none',
                }}
              ref={uploadInputRef}
              accept={pendingUploadAccept}
              multiple={pendingUploadMultiple}
              type="file"
              onChange={handleUploadFileSelect}
            />

            <CameraModal
              contentType={cameraContentType}
              isOpen={cameraOpen}
              setOpen={setCameraOpen}
              onContentCreated={handleCameraContentCreated}
            />
        </div>
    );
});

NodeRichTextFieldEditor.displayName = 'NodeRichTextFieldEditor';

/**
 * NodeRichTextField
 *
 * Schema rich text field. In readOnly / previewMode (visitor Peek), renders
 * display-only content via RichTextRenderer — no toolbar, insert, or edits.
 *
 * @param {boolean} [autoFocus=false] - Focus the editor when ready.
 * @param {string|number} [autoFocusKey] - Refocus token (e.g. node key) while autoFocus is on.
 * @param {object} field - Field definition.
 * @param {string} fieldLabel - Display label for the field.
 * @param {function} onChange - Called with normalized rich text value.
 * @param {object|null} value - Current field value.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} The rendered field.
 */
const NodeRichTextField = React.memo(({
    autoFocus = false,
    autoFocusKey = '',
    field,
    fieldLabel,
    onChange,
    value,
    readOnly = false,
    previewMode = false,
}) => {
    const isReadOnly = Boolean(readOnly || previewMode);

    if (isReadOnly) {
        return (
            <div className="node-rich-text-field-container is-readonly">
                <RichTextRenderer compact value={value} />
            </div>
        );
    }

    return (
        <NodeRichTextFieldEditor
          autoFocus={autoFocus}
          autoFocusKey={autoFocusKey}
          field={field}
          fieldLabel={fieldLabel}
          onChange={onChange}
          value={value}
        />
    );
});

NodeRichTextField.displayName = 'NodeRichTextField';

export default NodeRichTextField;
